import { AppContextType } from 'marketplace-ui-common/types';
import { ProductType } from 'marketplace-ui-common/types/products';
import { useListInstantiationRequestsQuery } from '../../__generated__/chirp/com/hubspot/automation/templatesrpc/TemplateInstantiationService.hooks';
import { useResolveWorkflowLinksQuery } from '../../__generated__/chirp/com/hubspot/automation/embedded/rpc/AutomationEmbeddedServiceV2.hooks';
import { useMemo } from 'react';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { gate } from 'hub-http/gates';
import {
  getResolvedWorkflowPath,
  getWorkflowFlowUrl,
} from 'marketplace-ui-common/utils/DetailsPageUrls';
import { getUserHasEmbeddedAutomationAccess } from 'marketplace-ui-common/utils/scopes';

interface UseGetProductUsageProps {
  appContext: AppContextType;
  productType: ProductType;
  sourceId: string | number;
  skip?: boolean;
}

export interface ProductUsage {
  createdAt: number;
  createdBy: number;
  href: string;
  workflowName: string;
}

interface UseGetProductUsageResponse {
  usages: ProductUsage[];
  loading: boolean;
  error: Error | undefined;
  refetch?: () => Promise<any>;
}

// This hook only supports workflow template usage at the moment, possibly will be used for agents in near future
export function useGetProductUsage({
  appContext,
  productType,
  sourceId,
  skip,
}: UseGetProductUsageProps): UseGetProductUsageResponse {
  const { userInfo } = useDetailsPageContext();
  const sourceIdAsNumber =
    typeof sourceId === 'string' ? parseInt(sourceId, 10) : sourceId;

  const isUngatedForUnifiedBuilder = userInfo?.gates?.includes(
    gate('Workflows:ModernEditor')
  );

  const hasEmbeddedAutomationAccess =
    getUserHasEmbeddedAutomationAccess(userInfo);

  const {
    data: templateInstancesData,
    loading: templateInstancesLoading,
    error: templateInstancesError,
    refetch: refetchTemplateInstances,
  } = useListInstantiationRequestsQuery({
    variables: {
      templateId: sourceIdAsNumber,
    },
    skip: skip || appContext !== 'app' || productType !== 'WORKFLOW_TEMPLATE',
  });

  const flowIdsToResolve = useMemo(
    () =>
      templateInstancesData?.results
        ?.filter(
          (instance) =>
            instance.status === 'COMPLETED' &&
            !instance.viewLocation?.url &&
            Boolean(instance.flowId)
        )
        .map((instance) => instance.flowId as number) || [],
    [templateInstancesData?.results]
  );

  const { data: workflowLinksData, loading: workflowLinksLoading } =
    useResolveWorkflowLinksQuery({
      variables: {
        flowIds: flowIdsToResolve,
      },
      skip: flowIdsToResolve.length === 0 || appContext !== 'app',
    });

  const parsedTemplateInstances = useMemo(() => {
    return templateInstancesData?.results
      ?.filter((instance) => instance.status === 'COMPLETED')
      .map((instance) => {
        const resolvedPath = getResolvedWorkflowPath(
          workflowLinksData?.links.find(
            ({ flowId }) => flowId === instance.flowId
          )
        );

        return {
          href:
            (!hasEmbeddedAutomationAccess && instance.viewLocation?.url) ||
            (!hasEmbeddedAutomationAccess && resolvedPath) ||
            getWorkflowFlowUrl(
              instance.flowId || 0,
              isUngatedForUnifiedBuilder,
              instance.targetType,
              hasEmbeddedAutomationAccess
            ),
          createdBy: instance.userId,
          createdAt: instance.createdAt,
          workflowName: instance.workflowName,
        } as ProductUsage;
      });
  }, [
    hasEmbeddedAutomationAccess,
    isUngatedForUnifiedBuilder,
    templateInstancesData?.results,
    workflowLinksData?.links,
  ]);

  switch (productType) {
    case 'WORKFLOW_TEMPLATE':
      return {
        usages: parsedTemplateInstances || [],
        loading: templateInstancesLoading || workflowLinksLoading,
        error: templateInstancesError,
        refetch: refetchTemplateInstances,
      };
    default:
      break;
  }

  return {
    usages: [],
    loading: false,
    error: undefined,
    refetch: undefined,
  };
}
